import { Button, Dropdown } from 'antd';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { useContext, useMemo, useRef, useState } from 'react';
import StackedGroupedChart from '../StackedGroupedChart';
import { groupBarChartWidth } from 'components/organism/PVMAnalysis/PVMInfiniteImpactChartContainer';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { calculateContainerHeight, formatNumWithKMBAlt, getConfig, scaleMaxValue } from 'utils/helperMethods';
import useProfitPoolEvolution from './hooks/useProfitPoolEvolution';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import { FilterContextData } from 'contexts/FilterContext';
import { UserContext } from 'contexts/ConfigContext';
import Barchart from 'components/molecules/Barchart';
import { isEmpty } from 'lodash';
import CustomDropdownButton from 'components/atoms/CustomDropdownButton';
import DownSvg from 'assets/svg/Down';

const ProfitPoolEvolutionChart = () => {
    const { appConfig } = useContext(UserContext);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { ppOverviewDropDownOptions, defaultCurrencySymbol, kpiLabelMapping, tiersColorMap } = profitPoolConfig;

    const ppOverviewUnitsList = useMemo(() => {
        return ppOverviewDropDownOptions.map((option: any) => {
            return {
                label: `${option.label.replace('$', defaultCurrencySymbol)}`,
                key: option.key,
            };
        });
    }, [ppOverviewDropDownOptions]);

    const containerWidth2 = useContainerWidth({
        containerId: 'profit-pool-evolution-chart',
    });
    const currentRef = useRef<any>(null);
    const chartHeight = calculateContainerHeight(3);

    const graphViewContainerWidth3 = Math.max(16 * groupBarChartWidth, containerWidth2);

    const [toLeft, setToLeft] = useState(0);
    const [selectedPPEvolutionUnit, setSelectedPPEvolutionUnit] = useState('profit_absolute');

    const { multiSelectedTiers, profitPoolTierList, allDimAndAttributeTypeList } = useContext(FilterContextData);

    const {
        data: profitPoolEvolutionData,
        isPending,
        isError,
        fetchNextPage,
        min,
        max,
    } = useProfitPoolEvolution(selectedPPEvolutionUnit);

    const minBound = useMemo(() => {
        return min < 0 ? min : 0;
    }, [min]);

    const maxBound = useMemo(() => {
        return max > 0 ? max : 0;
    }, [max]);

    // Prepare Excel export data
    const excelExportData = useMemo(() => {
        if (!profitPoolEvolutionData?.length) {
            return { headers: [], data: [] };
        }

        const selectedUnitLabel =
            ppOverviewUnitsList.find((option: any) => option.key === selectedPPEvolutionUnit)?.label ||
            kpiLabelMapping['profit'] ||
            'Profit';

        const headers = ['Period', 'Tier', selectedUnitLabel];

        const data = profitPoolEvolutionData.map((item: any) => [
            item.periodLabel || '',
            item.tier || '',
            item.profit_value || 0,
        ]);

        return { headers, data };
    }, [profitPoolEvolutionData, selectedPPEvolutionUnit, ppOverviewUnitsList, kpiLabelMapping]);

    if (isPending || allDimAndAttributeTypeList?.loading) {
        return (
            <>
                <p className="text-lg font-semibold">{'Profit Pool Evolution'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader />
                </div>
            </>
        );
    }

    if (isError) {
        return (
            <>
                <p className="text-lg font-semibold">{'Profit Pool Evolution'}</p>
                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox />
                </div>
            </>
        );
    }

    if (!isPending && isEmpty(profitPoolEvolutionData)) {
        return (
            <>
                <p className="text-lg font-semibold">{'Profit Pool Evolution'}</p>

                <div style={{ height: chartHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ErrorBox msg={'No Data'} />
                </div>
            </>
        );
    }

    return (
        <>
            <div className="flex flex-row items-center justify-between">
                <p className="text-lg font-semibold">{'Profit Pool Evolution'}</p>
                <div className="flex flex-row gap-x-2 items-center">
                    <Dropdown
                        menu={{
                            items: ppOverviewUnitsList,
                            onClick: (info) => {
                                setSelectedPPEvolutionUnit(info.key);
                            },
                        }}
                        trigger={['click']}
                        placement="bottomRight"
                        arrow={true}
                    >
                        <CustomDropdownButton type="default">
                            {ppOverviewUnitsList.find((item: any) => item.key === selectedPPEvolutionUnit)?.label ||
                                'Absolute $'}
                            <div className="ml-2">
                                <DownSvg />
                            </div>
                        </CustomDropdownButton>
                    </Dropdown>
                    <div className="flex flex-row gap-x-2 items-center">
                        <CopyToClipBoard refOfComponent={currentRef} />
                        <DownloadExcel
                            title={'Profit Pool Evolution'}
                            excelData={excelExportData.data}
                            headers={excelExportData.headers}
                        />
                    </div>
                </div>
            </div>

            {/* Legends */}
            <section ref={currentRef}>
                <div className="items-center flex flex-row justify-between my-2 mb-4">
                    <>
                        <div className="text-sm">
                            <p>{`Total ${kpiLabelMapping['profit']} ${defaultCurrencySymbol}`}</p>
                        </div>
                        <div className="flex flex-row gap-2 text-xs">
                            {['(YA)', ''].flatMap((_yearAgo) => {
                                return multiSelectedTiers.map((tier) => {
                                    return (
                                        <>
                                            <div
                                                className="w-3 h-2 self-center"
                                                style={{
                                                    backgroundColor: tiersColorMap?.[`${tier}${_yearAgo as string}`],
                                                }}
                                            />

                                            <p>{`${
                                                profitPoolTierList.find((t) => t.tierReference === tier)?.tierName
                                            } ${_yearAgo as string}`}</p>
                                        </>
                                    );
                                });
                            })}
                        </div>
                        <div />
                    </>
                </div>

                {/* Chart Section*/}
                <div className="flex">
                    <Barchart
                        padding={[0, 100, 15, 0]}
                        barWidth={0}
                        xAxisConfig={false}
                        dataset={[]}
                        height={chartHeight}
                        loading={false}
                        chartContainer={`profit-pool-evolution-chart-y1-axis`}
                        fields={['periodLabel', 'profit_value', 'tier']}
                        minBound={minBound}
                        maxBound={scaleMaxValue(20, maxBound)}
                        width={70}
                        colorKey={'tier'}
                        YGrid={false}
                        yAxisConfig={true}
                        yAxisLabelFormatter={(val: any) => {
                            return defaultCurrencySymbol + formatNumWithKMBAlt(val);
                        }}
                    />
                    <HorizontalInfiniteScroller
                        id={`profit-pool-evolution-chart-div`}
                        hasMore={true}
                        scrollThreshold={0.95}
                        toScrollWidth={toLeft}
                        onNext={(width: number) => {
                            // !isPending && fetchNextPage();
                        }}
                    >
                        <div
                            id={'profit-pool-evolution-chart-div'}
                            className="flex pb-[60px] overflow-x-auto max-w-full"
                        >
                            <StackedGroupedChart
                                containerId={'profit-pool-evolution-chart'}
                                height={chartHeight}
                                width={Math.max(
                                    graphViewContainerWidth3 * (profitPoolEvolutionData?.length / 60 || 1),
                                    graphViewContainerWidth3,
                                )}
                                yFieldMin={minBound}
                                yFieldMax={scaleMaxValue(20, maxBound)}
                                data={profitPoolEvolutionData}
                                xField={'periodLabel'}
                                yField={'profit_value'}
                                colorField={'tier'}
                                showTotalLabel={true}
                                totalField={'totalVal'}
                                fieldToShowTotalValueLabel={'tierReference'}
                                valuesOfTotalFieldToShowLabel={['tier1 (YA)', 'tier1']}
                                groupingField={'data_gran'}
                                tooltipFormatter={(xAxis: any, yAxis: any, color: any) => {
                                    return {
                                        name: color,
                                        value: defaultCurrencySymbol + formatNumWithKMBAlt(yAxis, 2, 2),
                                    };
                                }}
                                totalLabelFormatter={(val: number) => {
                                    return defaultCurrencySymbol + formatNumWithKMBAlt(val, 2, 2);
                                }}
                                colorMapping={{
                                    'CBI (YA)': '#E8EAFA',
                                    'CBBD (YA)': '#E8EAFA',
                                    'Distributor (YA)': '#D0D5F5',
                                    'Retailer (YA)': '#A2ABEA',
                                    CBBD: '#8EDFEE',
                                    CBI: '#8EDFEE',
                                    Distributor: '#4557D5',
                                    Retailer: '#9BDE91',
                                }}
                            />
                        </div>
                    </HorizontalInfiniteScroller>
                </div>
            </section>
        </>
    );
};

export default ProfitPoolEvolutionChart;
